import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSession } from '../hooks/useSession';
import { useActiveGame } from '../hooks/useActiveGame';
import { InstallButton } from './InstallButton';
import { KodeniCredit } from './KodeniCredit';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useSession();
  const active = useActiveGame(s => s.active);
  const clearActive = useActiveGame(s => s.clear);
  const nav = useNavigate();
  const loc = useLocation();
  const onGamePage = loc.pathname.startsWith('/game/');
  const showResume = !!active && !onGamePage;
  return (
    <div className={clsx('flex flex-col', onGamePage ? 'h-[100dvh]' : 'min-h-full')}>
      <header
        className="sticky top-0 z-30 bg-ink-900/85 backdrop-blur border-b border-ink-700/80"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg tracking-wide">
            <span className="inline-flex w-8 h-8 rounded-lg items-center justify-center text-ink-950 text-base shadow-glow"
              style={{ background: 'linear-gradient(180deg, #ffd06a 0%, #f5c14a 55%, #d39a23 100%)' }}>♠</span>
            <span className="text-accent">Durak</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-1">
            <NavItem to="/">Play</NavItem>
            <NavItem to="/stats">Stats</NavItem>
            {user && !user.isGuest && <NavItem to="/friends">Friends</NavItem>}
            {user && !user.isGuest && <NavItem to="/leaderboard">Leaderboard</NavItem>}
            <NavItem to="/settings">Settings</NavItem>
          </nav>
          <div className="flex items-center gap-2">
            <InstallButton className="btn-ghost !py-1 !px-2 text-xs" label="Install" />
            {user && (
              <div className="flex items-center gap-2">
                <span className="hidden sm:block text-sm text-ink-100">{user.name}</span>
                <button
                  className="btn-ghost !py-1 !px-2"
                  onClick={async () => { await logout(); clearActive(); nav('/login'); }}
                >Sign out</button>
              </div>
            )}
          </div>
        </div>
      </header>
      {showResume && active && (
        <div className="sticky top-[calc(env(safe-area-inset-top)+56px)] z-20 px-3 pt-1.5">
          <div className="w-full max-w-5xl mx-auto flex items-center gap-2 rounded-xl border border-accent/40 bg-ink-900/85 backdrop-blur px-3 py-2 shadow-lg">
            <button
              onClick={() => nav(`/game/${active.gameId}`)}
              className="flex items-center gap-2 flex-1 min-w-0 text-left"
            >
              <span className="inline-flex w-7 h-7 rounded-md bg-accent text-ink-950 items-center justify-center text-sm shadow-glow shrink-0">▶</span>
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-semibold truncate">
                  {active.phase === 'playing' ? 'Game in progress' : 'Waiting room'} — tap to resume
                </span>
                <span className="block text-[11px] text-ink-300 truncate">
                  Code <span className="font-mono text-accent">{active.code}</span> · {active.playerCount}/{active.maxPlayers}
                </span>
              </span>
            </button>
            <button
              onClick={clearActive}
              aria-label="Dismiss"
              className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center text-ink-300 hover:bg-ink-700/70 active:scale-95 transition"
            >×</button>
          </div>
        </div>
      )}
      <main className={clsx(
        'flex-1 min-h-0 max-w-5xl w-full mx-auto',
        onGamePage ? 'px-2 sm:px-4 py-2 flex flex-col' : 'px-4 py-4',
      )}>{children}</main>
      {!onGamePage && (
        <footer className="max-w-5xl w-full mx-auto px-4 py-4 flex justify-center">
          <KodeniCredit />
        </footer>
      )}
      <nav className={clsx(
        "sm:hidden sticky bottom-0 z-30 bg-ink-900/95 border-t border-ink-700/60 backdrop-blur",
        "pb-[max(env(safe-area-inset-bottom),8px)] shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.6)]",
        onGamePage && "hidden",
      )}>
        <div className={clsx('grid text-xs', user && !user.isGuest ? 'grid-cols-5' : 'grid-cols-3')}>
          <MobileItem to="/" label="Play" icon={<IconSpade />} />
          <MobileItem to="/stats" label="Stats" icon={<IconChart />} />
          {user && !user.isGuest && <MobileItem to="/friends" label="Friends" icon={<IconPeople />} />}
          {user && !user.isGuest && <MobileItem to="/leaderboard" label="Ranks" icon={<IconTrophy />} />}
          <MobileItem to="/settings" label="Settings" icon={<IconGear />} />
        </div>
      </nav>
    </div>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) => clsx(
        'px-3 py-1.5 rounded-lg text-sm font-medium transition',
        isActive ? 'bg-ink-700 text-accent' : 'text-ink-100 hover:bg-ink-700/60',
      )}
    >
      {children}
    </NavLink>
  );
}

function MobileItem({ to, label, icon }: { to: string; label: string; icon?: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) => clsx(
        'flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold uppercase tracking-wider transition',
        isActive ? 'text-accent' : 'text-ink-200',
      )}
    >
      <span className="block">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

function IconSpade() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2C8 6.5 4 10 4 14a5 5 0 0 0 9 3v3h-2v2h6v-2h-2v-3a5 5 0 0 0 9-3c0-4-4-7.5-8-12z" />
    </svg>
  );
}
function IconChart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}
function IconPeople() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.5 19a6.5 6.5 0 0 1 13 0" />
      <circle cx="17" cy="7" r="2.6" />
      <path d="M16 13.5a5 5 0 0 1 5.5 5.5" />
    </svg>
  );
}
function IconTrophy() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden>
      <path d="M6 4h12v3a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V4z" />
      <path d="M4 5h2v3a2 2 0 0 1-2-2V5zm16 0h-2v3a2 2 0 0 0 2-2V5z" />
      <path d="M9 14h6l-1 4h-4l-1-4z" />
      <path d="M8 20h8" />
    </svg>
  );
}
function IconGear() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
